<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="camera-overlay"
      @click.self="handleClose"
    >
      <div class="camera-container">
        <div class="camera-header">
          <div class="camera-title">{{ title || "扫码识别" }}</div>
          <button class="camera-close" type="button" @click="handleClose">
            关闭
          </button>
        </div>
        <div :id="regionId" class="camera-video">
          <video
            v-show="scanEngine !== 'html5'"
            ref="videoRef"
            class="native-video"
            playsinline
            webkit-playsinline
            muted
            autoplay
          />
          <div
            v-if="scanEngine === 'zxing' || scanEngine === 'native'"
            class="scan-guide"
            :class="boxModeClass"
          />
          <div v-if="openingHint" class="opening-hint">{{ openingHint }}</div>
        </div>
        <div class="camera-tip">
          {{ tipText || "对准二维码/条码，尽量靠近、光线充足" }}
        </div>
        <div v-if="confirmHint" class="camera-confirm">{{ confirmHint }}</div>
        <div v-if="rejectHint" class="camera-reject">{{ rejectHint }}</div>
        <div v-if="errorMsg" class="camera-error">{{ errorMsg }}</div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, watch, onUnmounted, nextTick, toRef } from "vue";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { HTMLCanvasElementLuminanceSource } from "@zxing/browser";
import {
  BinaryBitmap,
  HybridBinarizer,
  GlobalHistogramBinarizer,
  Code128Reader,
  Code39Reader,
  InvertedLuminanceSource,
} from "@zxing/library";

let regionSeq = 0;

function buildSquareBox(viewfinderWidth, viewfinderHeight) {
  const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
  const size = Math.max(240, Math.floor(minEdge * 0.85));
  return {
    width: Math.min(size, viewfinderWidth),
    height: Math.min(size, viewfinderHeight),
  };
}

function buildBarcodeBox(viewfinderWidth, viewfinderHeight) {
  const width = Math.max(280, Math.floor(viewfinderWidth * 0.92));
  const height = Math.max(
    90,
    Math.min(Math.floor(viewfinderHeight * 0.24), Math.floor(width * 0.26)),
  );
  return {
    width: Math.min(width, viewfinderWidth),
    height: Math.min(height, viewfinderHeight),
  };
}

const DEFAULT_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
];

const FORMAT_TO_NATIVE = {
  [Html5QrcodeSupportedFormats.QR_CODE]: "qr_code",
  [Html5QrcodeSupportedFormats.AZTEC]: "aztec",
  [Html5QrcodeSupportedFormats.CODABAR]: "codabar",
  [Html5QrcodeSupportedFormats.CODE_39]: "code_39",
  [Html5QrcodeSupportedFormats.CODE_93]: "code_93",
  [Html5QrcodeSupportedFormats.CODE_128]: "code_128",
  [Html5QrcodeSupportedFormats.DATA_MATRIX]: "data_matrix",
  [Html5QrcodeSupportedFormats.EAN_13]: "ean_13",
  [Html5QrcodeSupportedFormats.EAN_8]: "ean_8",
  [Html5QrcodeSupportedFormats.ITF]: "itf",
  [Html5QrcodeSupportedFormats.PDF_417]: "pdf417",
  [Html5QrcodeSupportedFormats.UPC_A]: "upc_a",
  [Html5QrcodeSupportedFormats.UPC_E]: "upc_e",
};

function toNativeFormats(formats) {
  const list = (formats || [])
    .map((f) => FORMAT_TO_NATIVE[f])
    .filter(Boolean);
  return [...new Set(list.length ? list : ["qr_code", "code_128", "code_39"])];
}

function buildOneDReaders(formats) {
  const set = new Set(formats || []);
  const readers = [];
  // 发运单几乎都是 CODE_128，优先它
  if (!formats?.length || set.has(Html5QrcodeSupportedFormats.CODE_128)) {
    readers.push(new Code128Reader());
  }
  if (set.has(Html5QrcodeSupportedFormats.CODE_39)) {
    readers.push(new Code39Reader());
  }
  if (!readers.length) readers.push(new Code128Reader());
  return readers;
}

async function canUseNativeDetector(formats) {
  if (typeof window === "undefined" || typeof window.BarcodeDetector !== "function") {
    return false;
  }
  try {
    if (typeof window.BarcodeDetector.getSupportedFormats === "function") {
      const supported = await window.BarcodeDetector.getSupportedFormats();
      const wanted = toNativeFormats(formats);
      return wanted.some((f) => supported.includes(f));
    }
    return true;
  } catch {
    return false;
  }
}

function normalizeScanText(decodedText) {
  return String(decodedText || "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[\r\n\t]/g, "")
    .trim();
}

export default {
  name: "CameraScan",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "扫码识别",
    },
    tipText: {
      type: String,
      default: "对准二维码/条码，尽量靠近、光线充足",
    },
    formats: {
      type: Array,
      default: () => DEFAULT_FORMATS,
    },
    /** square: 二维码；barcode: 一维条码（ZXing 专用） */
    boxMode: {
      type: String,
      default: "square",
      validator: (v) => ["square", "barcode"].includes(v),
    },
    facingMode: {
      type: String,
      default: "environment",
    },
    resultValidator: {
      type: Function,
      default: null,
    },
    /** 条形码需连续相同结果次数，降低误读 */
    confirmCount: {
      type: Number,
      default: 2,
    },
  },
  emits: ["close", "success", "error"],
  setup(props, { emit }) {
    const html5Qrcode = ref(null);
    const videoRef = ref(null);
    const errorMsg = ref("");
    const successLocked = ref(false);
    const rejectHint = ref("");
    const confirmHint = ref("");
    const openingHint = ref("");
    const scanEngine = ref("");
    const regionId = `camera-scan-region-${++regionSeq}`;

    let rejectHintTimer = 0;
    let detectTimer = 0;
    let mediaStream = null;
    let detectBusy = false;
    let pendingText = "";
    let pendingHits = 0;
    let cropCanvas = null;
    let cropCtx = null;
    let oneDReaders = [];

    const buildQrbox = (viewfinderWidth, viewfinderHeight) => {
      if (props.boxMode === "barcode") {
        return buildBarcodeBox(viewfinderWidth, viewfinderHeight);
      }
      return buildSquareBox(viewfinderWidth, viewfinderHeight);
    };

    const showRejectHint = (msg) => {
      rejectHint.value = msg || "识别异常，请重扫";
      if (rejectHintTimer) window.clearTimeout(rejectHintTimer);
      rejectHintTimer = window.setTimeout(() => {
        rejectHint.value = "";
      }, 1800);
    };

    const resetConfirmState = () => {
      pendingText = "";
      pendingHits = 0;
      confirmHint.value = "";
    };

    const passValidator = (text) => {
      if (typeof props.resultValidator !== "function") return true;
      try {
        return !!props.resultValidator(text);
      } catch {
        return false;
      }
    };

    const enhanceTrackFocus = async (track) => {
      try {
        const caps = track?.getCapabilities?.();
        if (!caps) return;
        const next = {};
        if (Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
          next.focusMode = "continuous";
        }
        if (Object.keys(next).length) {
          await track.applyConstraints({ advanced: [next] });
        }
      } catch {
        // ignore
      }
    };

    const stopZxingScan = () => {
      if (detectTimer) {
        window.clearTimeout(detectTimer);
        detectTimer = 0;
      }
      detectBusy = false;
      oneDReaders = [];
      cropCanvas = null;
      cropCtx = null;
      if (mediaStream) {
        mediaStream.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch {
            // ignore
          }
        });
        mediaStream = null;
      }
      const video = videoRef.value;
      if (video) {
        try {
          video.srcObject = null;
        } catch {
          // ignore
        }
      }
    };

    const openPreviewCamera = async () => {
      const video = videoRef.value;
      if (!video) throw new Error("相机节点未就绪");

      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      video.muted = true;
      video.autoplay = true;

      const attempts = [
        {
          audio: false,
          video: {
            facingMode: { ideal: props.facingMode },
            // 细密 CODE_128 需要更高分辨率
            width: { ideal: props.boxMode === "barcode" ? 1920 : 1280 },
            height: { ideal: props.boxMode === "barcode" ? 1080 : 720 },
          },
        },
        {
          audio: false,
          video: {
            facingMode: { ideal: props.facingMode },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        },
        {
          audio: false,
          video: {
            facingMode: { ideal: props.facingMode },
          },
        },
        {
          audio: false,
          video: true,
        },
      ];

      let lastError = null;
      for (const constraints of attempts) {
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
          lastError = null;
          break;
        } catch (e) {
          lastError = e;
        }
      }
      if (lastError || !mediaStream) {
        throw lastError || new Error("无法打开相机");
      }

      video.srcObject = mediaStream;
      try {
        await video.play();
      } catch {
        // 部分机型 play 会抛 AbortError，有流即可
      }

      await enhanceTrackFocus(mediaStream.getVideoTracks?.()[0]);
      return { video, stream: mediaStream };
    };

    const stopNativeScan = () => {
      if (detectTimer) {
        window.clearTimeout(detectTimer);
        detectTimer = 0;
      }
      detectBusy = false;
      if (mediaStream) {
        mediaStream.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch {
            // ignore
          }
        });
        mediaStream = null;
      }
      const video = videoRef.value;
      if (video) {
        try {
          video.srcObject = null;
        } catch {
          // ignore
        }
      }
    };

    const stopHtml5Scan = async () => {
      if (html5Qrcode.value) {
        try {
          await html5Qrcode.value.stop();
          await html5Qrcode.value.clear();
        } catch {
          // ignore
        }
        html5Qrcode.value = null;
      }
    };

    const stopScan = async () => {
      if (detectTimer) {
        window.clearTimeout(detectTimer);
        detectTimer = 0;
      }
      stopZxingScan();
      stopNativeScan();
      await stopHtml5Scan();
      scanEngine.value = "";
      successLocked.value = false;
      resetConfirmState();
    };

    const handleScanSuccess = (decodedText) => {
      const text = normalizeScanText(decodedText);
      if (!text) {
        successLocked.value = false;
        return;
      }

      if (!passValidator(text)) {
        successLocked.value = false;
        showRejectHint("识别异常，请对准发运单号重扫");
        resetConfirmState();
        return;
      }

      emit("success", text);
      handleClose();
    };

    /**
     * 条形码：连续命中同一结果才确认，避免偶发误读
     */
    const acceptBarcodeCandidate = (raw) => {
      const text = normalizeScanText(raw);
      if (!text) return;

      if (!passValidator(text)) {
        showRejectHint("识别异常，请对准条码重扫");
        resetConfirmState();
        return;
      }

      const need = Math.max(1, Number(props.confirmCount) || 2);
      if (text === pendingText) {
        pendingHits += 1;
      } else {
        pendingText = text;
        pendingHits = 1;
      }

      if (pendingHits < need) {
        confirmHint.value = `已识别，请保持对准…(${pendingHits}/${need})`;
        return;
      }

      if (successLocked.value) return;
      successLocked.value = true;
      confirmHint.value = "";
      handleScanSuccess(text);
    };

    const tryDecodeLuminance = (luminanceSource) => {
      const sources = [
        new HybridBinarizer(luminanceSource),
        new GlobalHistogramBinarizer(luminanceSource),
      ];
      // 反色再试一次（部分打印对比度异常）
      try {
        const inverted = new InvertedLuminanceSource(luminanceSource);
        sources.push(new HybridBinarizer(inverted));
      } catch {
        // ignore
      }

      for (let s = 0; s < sources.length; s += 1) {
        const bitmap = new BinaryBitmap(sources[s]);
        for (let r = 0; r < oneDReaders.length; r += 1) {
          try {
            const result = oneDReaders[r].decode(bitmap);
            const text = result?.getText?.();
            if (text) return text;
          } catch {
            // NotFound / Checksum — 继续
          }
          try {
            oneDReaders[r].reset?.();
          } catch {
            // ignore
          }
        }
      }
      return "";
    };

    const decodeBarcodeFromVideoFrame = (video) => {
      const vw = video.videoWidth || 0;
      const vh = video.videoHeight || 0;
      if (!vw || !vh) return "";

      if (!cropCanvas) {
        cropCanvas = document.createElement("canvas");
        cropCtx = cropCanvas.getContext("2d", {
          willReadFrequently: true,
          alpha: false,
        });
      }
      if (!cropCtx) return "";

      // 只取中间宽扁区域（和绿框一致），避免表格文字干扰
      const cropW = Math.max(360, Math.floor(vw * 0.92));
      const cropH = Math.max(100, Math.floor(vh * 0.22));
      const sx = Math.max(0, Math.floor((vw - cropW) / 2));
      const sy = Math.max(0, Math.floor((vh - cropH) / 2));
      // 放大细条，CODE_128 更容易解
      const scale = vw >= 1600 ? 1.5 : 2;
      const dw = Math.floor(cropW * scale);
      const dh = Math.floor(cropH * scale);

      if (cropCanvas.width !== dw || cropCanvas.height !== dh) {
        cropCanvas.width = dw;
        cropCanvas.height = dh;
      }

      cropCtx.drawImage(video, sx, sy, cropW, cropH, 0, 0, dw, dh);
      const luminance = new HTMLCanvasElementLuminanceSource(cropCanvas);
      return tryDecodeLuminance(luminance);
    };

    const startZxingBarcodeScan = async () => {
      openingHint.value = "正在打开相机…";
      scanEngine.value = "zxing";
      await nextTick();
      await new Promise((r) => requestAnimationFrame(() => r()));

      const { video } = await openPreviewCamera();
      openingHint.value = "";
      oneDReaders = buildOneDReaders(props.formats);

      const tick = () => {
        if (!props.visible || scanEngine.value !== "zxing") return;
        if (successLocked.value) return;

        if (!detectBusy && video.readyState >= 2) {
          detectBusy = true;
          try {
            const text = decodeBarcodeFromVideoFrame(video);
            if (text) acceptBarcodeCandidate(text);
          } catch {
            // ignore frame
          }
          detectBusy = false;
        }

        detectTimer = window.setTimeout(tick, 45);
      };
      tick();
    };

    const startNativeDetectLoop = (detector) => {
      const tick = async () => {
        if (!props.visible || scanEngine.value !== "native") return;
        const video = videoRef.value;
        if (!video || successLocked.value) {
          detectTimer = window.setTimeout(tick, 50);
          return;
        }

        if (!detectBusy && video.readyState >= 2) {
          detectBusy = true;
          try {
            const codes = await detector.detect(video);
            if (codes?.length && codes[0]?.rawValue && !successLocked.value) {
              successLocked.value = true;
              handleScanSuccess(codes[0].rawValue);
              detectBusy = false;
              return;
            }
          } catch {
            // ignore
          }
          detectBusy = false;
        }
        detectTimer = window.setTimeout(tick, 50);
      };
      tick();
    };

    const startNativeScan = async () => {
      openingHint.value = "正在打开相机…";
      scanEngine.value = "native";
      await nextTick();
      await new Promise((r) => requestAnimationFrame(() => r()));

      const { video } = await openPreviewCamera();
      openingHint.value = "";

      const detector = new window.BarcodeDetector({
        formats: toNativeFormats(props.formats),
      });
      startNativeDetectLoop(detector);
      // keep video reference used
      void video;
    };

    const enhanceHtml5Focus = async (scanner) => {
      try {
        const caps = scanner.getRunningTrackCapabilities?.();
        if (!caps) return;
        const next = {};
        if (Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
          next.focusMode = "continuous";
        }
        if (Object.keys(next).length) {
          await scanner.applyVideoConstraints(next);
        }
      } catch {
        // ignore
      }
    };

    const startHtml5Scan = async () => {
      scanEngine.value = "html5";
      await nextTick();

      const scanner = new Html5Qrcode(regionId, {
        formatsToSupport: props.formats,
        useBarCodeDetectorIfSupported: true,
        verbose: false,
      });
      html5Qrcode.value = scanner;

      const isBarcode = props.boxMode === "barcode";
      const baseScanConfig = {
        fps: 20,
        qrbox: buildQrbox,
        disableFlip: isBarcode,
      };

      const attempts = [
        {
          camera: { facingMode: props.facingMode },
          config: {
            ...baseScanConfig,
            videoConstraints: {
              facingMode: { ideal: props.facingMode },
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30 },
            },
          },
        },
        {
          camera: { facingMode: props.facingMode },
          config: baseScanConfig,
        },
      ];

      let lastError = null;
      for (const attempt of attempts) {
        try {
          await scanner.start(
            attempt.camera,
            attempt.config,
            (decodedText) => {
              if (!decodedText || successLocked.value) return;
              if (isBarcode) {
                acceptBarcodeCandidate(decodedText);
              } else {
                successLocked.value = true;
                handleScanSuccess(decodedText);
              }
            },
            () => {},
          );
          lastError = null;
          break;
        } catch (e) {
          lastError = e;
          try {
            if (scanner.isScanning) await scanner.stop();
            await scanner.clear();
          } catch {
            // ignore
          }
        }
      }

      if (lastError) throw lastError;
      await enhanceHtml5Focus(scanner);
    };

    const startScan = async () => {
      if (!props.visible) return;

      errorMsg.value = "";
      rejectHint.value = "";
      confirmHint.value = "";
      openingHint.value = "";
      successLocked.value = false;
      resetConfirmState();

      await nextTick();

      try {
        // 条形码：专用 ZXing 一维解码（比浏览器 BarcodeDetector 稳）
        if (props.boxMode === "barcode") {
          try {
            await startZxingBarcodeScan();
            return;
          } catch (zxingErr) {
            stopZxingScan();
            scanEngine.value = "";
            openingHint.value = "";
            console.warn("[CameraScan] zxing barcode failed, fallback html5", zxingErr);
            await startHtml5Scan();
            return;
          }
        }

        // 二维码：原生优先（快）
        if (await canUseNativeDetector(props.formats)) {
          try {
            await startNativeScan();
            return;
          } catch (nativeErr) {
            stopNativeScan();
            scanEngine.value = "";
            openingHint.value = "";
            console.warn("[CameraScan] native failed, fallback html5", nativeErr);
          }
        }
        await startHtml5Scan();
      } catch (e) {
        openingHint.value = "";
        errorMsg.value =
          e?.name === "NotAllowedError"
            ? "请允许使用相机权限后重试"
            : e?.message === "timeout"
              ? "相机启动超时"
              : e?.message || "无法打开相机";
        emit("error", errorMsg.value);
      }
    };

    const handleClose = () => {
      if (rejectHintTimer) window.clearTimeout(rejectHintTimer);
      rejectHint.value = "";
      confirmHint.value = "";
      openingHint.value = "";
      stopScan();
      emit("close");
    };

    watch(
      () => props.visible,
      (newVal) => {
        if (newVal) startScan();
        else stopScan();
      },
    );

    onUnmounted(() => {
      stopScan();
    });

    return {
      regionId,
      videoRef,
      scanEngine,
      boxModeClass: toRef(props, "boxMode"),
      errorMsg,
      rejectHint,
      confirmHint,
      openingHint,
      handleClose,
    };
  },
};
</script>

<style scoped>
.camera-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.camera-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.camera-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background: rgba(0, 0, 0, 0.3);
}

.camera-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.camera-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
}

.camera-video {
  flex: 1;
  position: relative;
  background: #000;
  min-height: 0;
  overflow: hidden;
}

.native-video,
.camera-video :deep(video) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
  z-index: 1;
}

.scan-guide {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid rgba(91, 192, 222, 0.95);
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.35);
  pointer-events: none;
  z-index: 2;
}

.opening-hint {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 14px;
  z-index: 3;
  background: rgba(0, 0, 0, 0.45);
  padding: 8px 14px;
  border-radius: 8px;
}

.scan-guide.barcode {
  width: 92%;
  height: 22%;
  max-height: 130px;
}

.scan-guide.square {
  width: min(72vw, 320px);
  height: min(72vw, 320px);
}

.camera-tip {
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  padding: 12px;
}

.camera-confirm {
  text-align: center;
  color: #86efac;
  font-size: 14px;
  padding: 8px 12px;
  background: rgba(34, 197, 94, 0.15);
  margin: 0 12px 12px;
  border-radius: 8px;
}

.camera-reject {
  text-align: center;
  color: #fbbf24;
  font-size: 14px;
  padding: 8px 12px;
  background: rgba(251, 191, 36, 0.15);
  margin: 0 12px 12px;
  border-radius: 8px;
}

.camera-error {
  text-align: center;
  color: #ef4444;
  font-size: 14px;
  padding: 8px 12px;
  background: rgba(239, 68, 68, 0.15);
  margin: 0 12px 12px;
  border-radius: 8px;
}
</style>
